import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { merge } from 'lodash';
import { setScenario, setParentOrigin } from '../store/slices/scenarioSlice';
import { setTestName, setLanguage } from '../store/slices/userSlice';
import { useTranslation } from 'react-i18next';
import {
  setActionButtonText,
  setShowActionButton,
  setShowSupport,
  setSupportURI,
} from '../store/slices/contentSlice';
import { validSupportURI } from '../utils/functions';

export const useMessageHandler = (setColorTheme) => {
  const dispatch = useDispatch();
  const parentOriginRef = useRef(null);
  const { i18n } = useTranslation();

  const handleMessage = useCallback(
    (event) => {
      if (!event.data) return;
      const { type, payload } = event.data;

      if (!parentOriginRef.current && type === 'originHandshake') {
        parentOriginRef.current = event.origin;
        dispatch(setParentOrigin(event.origin));
        return;
      }

      if (!type || !payload) return;
      if (event.origin !== parentOriginRef.current) return;

      switch (type) {
        case 'scenario':
          dispatch(setScenario(payload));
          break;
        case 'theme':
          try {
            const parsed = JSON.parse(payload);
            setColorTheme((prevTheme) => merge({}, prevTheme, parsed));
          } catch (error) {
            console.error('Failed to parse theme:', error);
          }
          break;
        case 'background':
          setColorTheme((prevTheme) =>
            merge({}, prevTheme, {
              palette: { background: { default: payload } },
            })
          );
          break;
        case 'testName':
          dispatch(setTestName(payload));
          break;
        case 'language':
          dispatch(setLanguage(payload));
          i18n.changeLanguage(payload);
          break;
        case 'actionButtonText':
          dispatch(setActionButtonText(payload));
          break;
        case 'supportURI':
          if (validSupportURI(payload, parentOriginRef.current)) {
            dispatch(setSupportURI(payload));
          }
          break;
        case 'showSupport':
        case 'showActionButton':
          try {
            const parsed = JSON.parse(payload);
            const status =
              parsed?.status !== undefined ? Boolean(parsed.status) : true;
            type === 'showSupport'
              ? dispatch(setShowSupport(status))
              : dispatch(setShowActionButton(status));
          } catch (error) {
            console.error('Failed to parse payload:', error);
          }
          break;
        default:
          break;
      }
    },
    [dispatch, i18n, setColorTheme]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
};
